import {
    AggregationSurveyResponsesSeries,
    AggregationSurveySummary,
} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {comparisonOf} from "../../../insights/kit/Headline";
import {StatCardData} from "../../../insights/kit/StatCard";
import {formatSurveyFillOutTime, formatSurveyTimeToRespond} from "../../insights/base/surveyDurations";

type Translate = (key: string, options?: Record<string, unknown>) => string;

type Format = (value: number) => string;

const round = (value: number) => Math.round(value);

const count: Format = value => String(round(value));

const percent: Format = value => `${round(value)}%`;

const card = (
    t: Translate,
    titleKey: string,
    hintKey: string,
    value: number | null | undefined,
    format: Format,
    previous?: number | null,
    lowerIsBetter = false
): StatCardData => ({
    id: titleKey,
    title: t(titleKey),
    hint: t(hintKey),
    value: value == null ? '—' : format(value),
    comparison: comparisonOf(value, previous, format, lowerIsBetter),
});

/** Check-ins and check-outs over the range, counted from the stays the daily list reads. */
export const toStayStatCards = (summary: AggregationSurveySummary | undefined, t: Translate): StatCardData[] => {
    // Unknown until the summary lands: the skeleton shows both, and an organisation without stays then drops them.
    if (summary != null && summary.current.checkIns == null) return [];

    return [
        card(
            t,
            'Check-ins',
            'Stays arriving in the period, cancelled and no-show ones excluded.',
            summary?.current.checkIns,
            count,
            summary?.previous?.checkIns
        ),
        card(
            t,
            'Check-outs',
            'Stays leaving in the period, cancelled and no-show ones excluded.',
            summary?.current.checkOuts,
            count,
            summary?.previous?.checkOuts
        ),
    ];
};

export const toPeriodStatCards = (summary: AggregationSurveySummary | undefined, t: Translate): StatCardData[] => {
    const current = summary?.current;
    const previous = summary?.previous;

    const timeToRespond: Format = seconds => formatSurveyTimeToRespond(seconds, t);
    const fillOutTime: Format = seconds => formatSurveyFillOutTime(seconds, t);

    return [
        card(
            t,
            'Invitations',
            'Invitations sent in the period over every survey, voided ones excluded.',
            current?.invitations,
            count,
            previous?.invitations
        ),
        card(
            t,
            'Submissions',
            'Surveys submitted in the period, over every survey.',
            current?.submissions,
            count,
            previous?.submissions
        ),
        card(
            t,
            'Response rate',
            'Submissions as a share of the invitations sent in the period. Surveys that send no invitations add to neither figure.',
            current?.responseRate,
            percent,
            previous?.responseRate
        ),
        card(
            t,
            'Completion rate',
            'Fill-outs that were sent off, as a share of the fill-outs that started in the period.',
            current?.completionRate,
            percent,
            previous?.completionRate
        ),
        card(
            t,
            'Time to respond',
            'Median time from invitation to submission.',
            current?.medianTimeToRespondSeconds,
            timeToRespond,
            previous?.medianTimeToRespondSeconds,
            true
        ),
        card(
            t,
            'Fill-out time',
            'Median time from first answer to submission.',
            current?.medianFillOutSeconds,
            fillOutTime,
            previous?.medianFillOutSeconds,
            true
        ),
    ];
};

export const toResponsesSeries = (summary: AggregationSurveySummary | undefined): AggregationSurveyResponsesSeries | undefined => {
    if (summary == null) return undefined;

    const {range, compareRange, current, previous, days, compareDays} = summary;

    const points = (entries: AggregationSurveySummary['days']) =>
        entries.map(day => ({day: day.day, responses: day.submissions, invitations: day.invitations}));

    return {
        summary: {
            responses: current.submissions,
            invitations: current.invitations,
            previousResponses: previous?.submissions ?? null,
            previousInvitations: previous?.invitations ?? null,
            perDay: current.submissions / range.days,
            previousPerDay: previous != null && compareRange != null ? previous.submissions / compareRange.days : null,
            target: null,
        },
        current: {from: range.from, to: range.to, points: points(days)},
        compare: compareRange != null && compareDays != null
            ? {from: compareRange.from, to: compareRange.to, points: points(compareDays)}
            : null,
    };
};